import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare, CheckCircle2, ChevronDown, Trash2, AlertCircle } from 'lucide-react';
import { useLocalStorage } from 'react-use';
import { createProjectComment, fetchProjectComments, replyProjectComment, toggleLikeComment, deleteProjectComment, fetchTbUserByNik, getUserByToken } from '../../../services/projectReportServices';

const getAvatarColor = (name) => {
  return `hsl(${(name?.length * 40 || 0) % 360}, 70%, 50%)`;
};

const getInitials = (name) => {
  if (!name) return 'U';
  return name.substring(0, 2).toUpperCase();
};

const formatCommentTime = (dateObj = new Date()) => {
  const d = dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const t = dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${d} at ${t}`;
};

const ProjectComments = ({ projectId, accent = '#14b8a6', nik = null }) => {
  const [comments, setComments] = useState([]);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, commentId: null, isReply: false, parentId: null, isDeleting: false });
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });

  const toggleReplies = (commentId) => {
    setExpandedReplies(prev => ({
      ...prev,
      [commentId]: !prev[commentId]
    }));
  };

  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [user, setUser] = useState(null);
  const [token, _] = useLocalStorage('token', '');

  const fetchComments = async () => {
    const response = await fetchProjectComments(projectId);
    setComments(response.data || response);
  }

  const fetchUser = async () => {
    const userToken = token;
    const response = await (nik ? fetchTbUserByNik(nik) : getUserByToken(userToken));
    if (response && (response.status === true || response.status === 'success' || response.status === 200)) {
      const userData = Array.isArray(response.data) ? response.data[0] : response.data;
      setUser(userData);
    }
  };

  useEffect(() => {
    fetchComments();
    fetchUser();
  }, []);

  const handleToggleReaction = async (commentId, type, isReply = false, parentId = null) => {
    let updatedPayload = null;
    let targetParentId = isReply ? parentId : commentId;

    const processReaction = (item) => {
      let newlyLiked = item.userLiked;
      let newlyDisliked = item.userDisliked;
      let newLikes = item.likes;
      let newDislikes = item.dislikes;

      if (type === 'like') {
        newlyLiked = !item.userLiked;
        newLikes = item.likes + (newlyLiked ? 1 : -1);
        newDislikes = item.userDisliked ? item.dislikes - 1 : item.dislikes;
        newlyDisliked = false;
      } else if (type === 'dislike') {
        newlyDisliked = !item.userDisliked;
        newDislikes = item.dislikes + (newlyDisliked ? 1 : -1);
        newLikes = item.userLiked ? item.likes - 1 : item.likes;
        newlyLiked = false;
      }

      return {
        likes: newLikes,
        dislikes: newDislikes,
        userLiked: newlyLiked,
        userDisliked: newlyDisliked
      };
    };

    setComments(comments.map(c => {
      if (!isReply && c.id === commentId) {
        updatedPayload = processReaction(c);

        return {
          ...c,
          ...updatedPayload
        };
      } else if (isReply && c.id === parentId) {
        return {
          ...c,
          replies: c.replies.map(r => {
            if (r.id === commentId) {
              updatedPayload = {
                ...processReaction(r),
                replyId: commentId
              };

              return {
                ...r,
                ...updatedPayload
              };
            }
            return r;
          })
        };
      }
      return c;
    }));

    if (updatedPayload) {
      await toggleLikeComment(targetParentId, updatedPayload);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !(user?.nama_lengkap || user?.nama_user)) return;
    const newEntry = {
      user: { name: (user?.nama_lengkap || user?.nama_user), nik: user?.nik, jabatan: (user?.nama_jabatan || '-'), isVerified: (user?.kode_bagian === "BOD") },
      time: formatCommentTime(),
      comment: newComment,
      projectId: parseInt(projectId),
      likes: 0,
      dislikes: 0,
      userLiked: false,
      userDisliked: false
    };
    const response = await createProjectComment(newEntry);
    const createdComment = response?.data || response;

    setComments([createdComment, ...comments]);
    setNewComment('');
  };

  const handleAddReply = async (commentId, replyTargetName = null) => {
    if (!replyText.trim() || !(user?.nama_lengkap || user?.nama_user)) return;
    const newReply = {
      user: { name: (user?.nama_lengkap || user?.nama_user), nik: user?.nik, jabatan: (user?.nama_jabatan || '-'), isVerified: (user?.kode_bagian === "BOD") },
      time: formatCommentTime(),
      comment: replyText,
      replyTargetName: replyTargetName,
      projectId: parseInt(projectId),
      likes: 0,
      dislikes: 0,
      userLiked: false,
      userDisliked: false
    };

    const response = await replyProjectComment(commentId, newReply);
    const createdReply = response?.data || response;

    setComments(comments.map(c => {
      if (c.id === commentId) {
        return createdReply;
      }
      return c;
    }));

    // Otomatis expand balasan jika belum terbuka agar user bisa melihat balasannya
    setExpandedReplies(prev => ({
      ...prev,
      [commentId]: true
    }));

    setReplyingTo(null);
    setReplyText('');
  };

  const requestDelete = (commentId, isReply = false, parentId = null) => {
    setDeleteModal({ isOpen: true, commentId, isReply, parentId, isDeleting: false });
  };

  const confirmDelete = async () => {
    setDeleteModal(prev => ({ ...prev, isDeleting: true }));
    const { commentId, isReply, parentId } = deleteModal;

    const result = await deleteProjectComment(commentId, isReply, parentId);

    if (result) {
      setComments(prevComments => {
        if (!isReply) {
          return prevComments.filter(c => c.id !== commentId);
        } else {
          return prevComments.map(c => {
            if (c.id === parentId) {
              return {
                ...c,
                replies: c.replies.filter(r => r.id !== commentId)
              };
            }
            return c;
          });
        }
      });
      setDeleteModal({ isOpen: false, commentId: null, isReply: false, parentId: null, isDeleting: false });
    } else {
      setDeleteModal({ isOpen: false, commentId: null, isReply: false, parentId: null, isDeleting: false });
      setErrorModal({ isOpen: true, message: "Terjadi kesalahan saat menghapus komentar dari server. Silakan coba lagi nanti." });
    }
  };

  const cancelDelete = () => {
    if (deleteModal.isDeleting) return;
    setDeleteModal({ isOpen: false, commentId: null, isReply: false, parentId: null, isDeleting: false });
  };

  const Avatar = ({ name, isBrand }) => (
    <div style={{
      width: '40px', height: '40px', borderRadius: '50%',
      background: isBrand ? '#ea580c' : getAvatarColor(name),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '14px', fontWeight: '700', color: '#fff',
      flexShrink: 0
    }}>
      {isBrand ? <span style={{ fontStyle: 'italic', fontWeight: '900' }}>S</span> : getInitials(name)}
    </div>
  );

  return (
    <div style={{
      background: '#fff', borderRadius: '20px', padding: '32px',
      boxShadow: '0 4px 24px rgba(0,0,0,0.06)', border: `1px solid #f1f5f9`,
      marginTop: '24px', fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Comments</h2>
          <div style={{ background: '#ea580c', color: '#fff', padding: '2px 10px', borderRadius: '12px', fontSize: '13px', fontWeight: '700' }}>
            {comments.reduce((acc, curr) => acc + 1 + (curr.replies?.length || 0), 0)}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '14px' }}>
        <Avatar name={user?.nama_lengkap || user?.nama_user} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <textarea
            placeholder={`Add a comment as ${user?.nama_lengkap || user?.nama_user}...`}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            style={{
              width: '100%', minHeight: '90px', padding: '14px 16px',
              borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc',
              fontSize: '14px', fontFamily: 'inherit', resize: 'vertical',
              outline: 'none', transition: 'border 0.2s', boxSizing: 'border-box'
            }}
            onFocus={(e) => { e.target.style.borderColor = accent; e.target.style.backgroundColor = '#fff'; }}
            onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.backgroundColor = '#f8fafc'; }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleAddComment}
              disabled={!(user?.nama_lengkap || user?.nama_user) || !newComment.trim()}
              style={{
                background: ((user?.nama_lengkap || user?.nama_user) && newComment.trim()) ? accent : '#cbd5e1',
                color: '#fff', border: 'none', padding: '10px 24px',
                borderRadius: '20px', fontSize: '14px', fontWeight: '600',
                cursor: ((user?.nama_lengkap || user?.nama_user) && newComment.trim()) ? 'pointer' : 'not-allowed',
                transition: 'all 0.2s'
              }}
            >
              Comment
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        {comments.map((comment) => (
          <div key={comment.id} style={{ display: 'flex', gap: '16px' }}>
            <Avatar name={comment.user.name} />
            <div style={{ flex: 1 }}>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{comment.user.name}</span>
                <span style={{ fontSize: '13px', color: '#64748b' }}>{comment.time}</span>
              </div>

              <p style={{ fontSize: '15px', color: '#1e293b', margin: '0 0 14px 0', lineHeight: 1.6 }}>
                {comment.comment}
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                {/* <div 
                  onClick={() => handleToggleReaction(comment.id, 'like')}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: comment.userLiked ? accent : (comment.likes > 0 ? '#ea580c' : '#64748b') }}
                >
                  <ThumbsUp size={16} fill={comment.userLiked ? accent : (comment.likes > 0 ? '#ea580c' : 'none')} /> <span style={{ fontSize: '13px', fontWeight: '700' }}>{comment.likes > 0 ? comment.likes : ''}</span>
                </div>
                <div 
                  onClick={() => handleToggleReaction(comment.id, 'dislike')}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: comment.userDisliked ? '#334155' : '#64748b' }}
                >
                  <ThumbsDown size={16} fill={comment.userDisliked ? '#334155' : 'none'} /> <span style={{ fontSize: '13px', fontWeight: '700' }}>{comment.dislikes > 0 ? comment.dislikes : ''}</span>
                </div> */}
                <div
                  onClick={() => {
                    if (replyingTo === comment.id) {
                      setReplyingTo(null);
                      setReplyText('');
                    } else {
                      setReplyingTo(comment.id);
                      setReplyText('');
                    }
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#64748b', fontSize: '13px', fontWeight: '700', transition: 'color 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#334155'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                >
                  <MessageSquare size={16} /> Reply
                </div>
                {comment.user.name === (user?.nama_lengkap || user?.nama_user) && (
                  <div
                    onClick={() => requestDelete(comment.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#ef4444', fontSize: '13px', fontWeight: '700', transition: 'opacity 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    <Trash2 size={16} /> Delete
                  </div>
                )}
              </div>

              {replyingTo === comment.id && (
                <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
                  <div style={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}><Avatar name={user?.nama_lengkap || user?.nama_user} /></div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '-4px' }}>
                      Replying to <span style={{ color: accent, fontWeight: '700' }}>{comment.user.name}</span>
                    </div>
                    <textarea
                      autoFocus
                      placeholder={`Write your reply...`}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      style={{
                        width: '100%', minHeight: '70px', padding: '12px 14px',
                        borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc',
                        fontSize: '14px', fontFamily: 'inherit', resize: 'vertical',
                        outline: 'none', transition: 'border 0.2s', boxSizing: 'border-box'
                      }}
                      onFocus={(e) => { e.target.style.borderColor = accent; e.target.style.backgroundColor = '#fff'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.backgroundColor = '#f8fafc'; }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <button
                        onClick={() => { setReplyingTo(null); setReplyText(''); }}
                        style={{
                          background: 'transparent', color: '#64748b', border: 'none',
                          padding: '8px 16px', borderRadius: '20px', fontSize: '13px',
                          fontWeight: '600', cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleAddReply(comment.id)}
                        disabled={!(user?.nama_lengkap || user?.nama_user) || !replyText.trim()}
                        style={{
                          background: (user?.nama_lengkap || user?.nama_user && replyText.trim()) ? accent : '#cbd5e1',
                          color: '#fff', border: 'none', padding: '8px 20px',
                          borderRadius: '20px', fontSize: '13px', fontWeight: '600',
                          cursor: (user?.nama_lengkap || user?.nama_user && replyText.trim()) ? 'pointer' : 'not-allowed',
                        }}
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {comment.replies && comment.replies.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <div
                    onClick={() => toggleReplies(comment.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      cursor: 'pointer', color: '#64748b', fontSize: '14px',
                      fontWeight: '700', marginLeft: '8px', marginBottom: expandedReplies[comment.id] ? '24px' : '0'
                    }}
                  >
                    <div style={{ width: '32px', height: '1.5px', background: '#e2e8f0' }} />
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', transition: 'color 0.2s' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#334155'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                    >
                      {expandedReplies[comment.id] ? 'Hide replies' : `${comment.replies.length} replies`}
                      <ChevronDown size={18} style={{ transform: expandedReplies[comment.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                    </span>
                  </div>

                  {expandedReplies[comment.id] && (
                    <div style={{ position: 'relative' }}>
                      <div style={{
                        position: 'absolute', left: '-36px', top: '-40px', bottom: '20px',
                        width: '18px', borderLeft: '2px solid #e2e8f0', borderBottom: '2px solid #e2e8f0',
                        borderBottomLeftRadius: '16px'
                      }} />

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        {comment.replies.map((reply) => (
                          <div key={reply.id} style={{ display: 'flex', gap: '16px', position: 'relative' }}>
                            <div style={{ zIndex: 1, background: '#fff' }}>
                              <Avatar name={reply.user.name} isBrand={reply.user.isBrand} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{reply.user.name}</span>
                                {reply.user.isVerified && <CheckCircle2 size={15} color="#3b82f6" fill="#bfdbfe" />}
                                <span style={{ fontSize: '13px', color: '#64748b' }}>{reply.time}</span>
                              </div>
                              <p style={{ fontSize: '15px', color: '#1e293b', margin: '0 0 14px 0', lineHeight: 1.6 }}>
                                {reply.replyTargetName && (
                                  <span style={{ display: 'inline-block', color: accent, fontWeight: '700', marginRight: '6px', background: `${accent}15`, padding: '2px 8px', borderRadius: '12px', fontSize: '13px' }}>
                                    @{reply.replyTargetName}
                                  </span>
                                )}
                                {reply.comment}
                              </p>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                {/* <div 
                                  onClick={() => handleToggleReaction(reply.id, 'like', true, comment.id)}
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: reply.userLiked ? accent : (reply.likes > 0 ? '#ea580c' : '#64748b') }}
                                >
                                  <ThumbsUp size={16} fill={reply.userLiked ? accent : (reply.likes > 0 ? '#ea580c' : 'none')} /> <span style={{ fontSize: '13px', fontWeight: '700' }}>{reply.likes > 0 ? reply.likes : ''}</span>
                                </div>
                                <div 
                                  onClick={() => handleToggleReaction(reply.id, 'dislike', true, comment.id)}
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: reply.userDisliked ? '#334155' : '#64748b' }}
                                >
                                  <ThumbsDown size={16} fill={reply.userDisliked ? '#334155' : 'none'} /> <span style={{ fontSize: '13px', fontWeight: '700' }}>{reply.dislikes > 0 ? reply.dislikes : ''}</span>
                                </div> */}
                                <div
                                  onClick={() => {
                                    if (replyingTo === reply.id) {
                                      setReplyingTo(null);
                                      setReplyText('');
                                    } else {
                                      setReplyingTo(reply.id);
                                      setReplyText('');
                                    }
                                  }}
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#64748b', fontSize: '13px', fontWeight: '700' }}>
                                  <MessageSquare size={16} /> Reply
                                </div>
                                {reply.user.name === (user?.nama_lengkap || user?.nama_user) && (
                                  <div
                                    onClick={() => requestDelete(reply.id, true, comment.id)}
                                    style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#ef4444', fontSize: '13px', fontWeight: '700', transition: 'opacity 0.2s' }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                  >
                                    <Trash2 size={16} /> Delete
                                  </div>
                                )}
                              </div>

                              {replyingTo === reply.id && (
                                <div style={{ marginTop: '20px', display: 'flex', gap: '12px' }}>
                                  <div style={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}><Avatar name={user?.nama_lengkap || user?.nama_user} /></div>
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '500', marginBottom: '-4px' }}>
                                      Replying to <span style={{ color: accent, fontWeight: '700' }}>@{reply.user.name}</span>
                                    </div>
                                    <textarea
                                      autoFocus
                                      placeholder={`Write your reply...`}
                                      value={replyText}
                                      onChange={(e) => setReplyText(e.target.value)}
                                      style={{
                                        width: '100%', minHeight: '70px', padding: '12px 14px',
                                        borderRadius: '12px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc',
                                        fontSize: '14px', fontFamily: 'inherit', resize: 'vertical',
                                        outline: 'none', transition: 'border 0.2s', boxSizing: 'border-box'
                                      }}
                                      onFocus={(e) => { e.target.style.borderColor = accent; e.target.style.backgroundColor = '#fff'; }}
                                      onBlur={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.backgroundColor = '#f8fafc'; }}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                      <button
                                        onClick={() => { setReplyingTo(null); setReplyText(''); }}
                                        style={{
                                          background: 'transparent', color: '#64748b', border: 'none',
                                          padding: '8px 16px', borderRadius: '20px', fontSize: '13px',
                                          fontWeight: '600', cursor: 'pointer'
                                        }}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => handleAddReply(comment.id, reply.user.name)}
                                        disabled={!(user?.nama_lengkap || user?.nama_user) || !replyText.trim()}
                                        style={{
                                          background: ((user?.nama_lengkap || user?.nama_user) && replyText.trim()) ? accent : '#cbd5e1',
                                          color: '#fff', border: 'none', padding: '8px 20px',
                                          borderRadius: '20px', fontSize: '13px', fontWeight: '600',
                                          cursor: ((user?.nama_lengkap || user?.nama_user) && replyText.trim()) ? 'pointer' : 'not-allowed',
                                        }}
                                      >
                                        Reply
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modern Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: '24px', padding: '32px', width: '90%', maxWidth: '400px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            fontFamily: "'Inter', sans-serif"
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%', background: '#fef2f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px'
            }}>
              <Trash2 size={24} color="#ef4444" />
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Hapus Komentar?</h3>
            <p style={{ margin: '0 0 32px 0', fontSize: '15px', color: '#64748b', lineHeight: '1.6' }}>
              Komentar ini akan dihapus secara permanen dan tidak dapat dikembalikan lagi. Anda yakin?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={cancelDelete}
                disabled={deleteModal.isDeleting}
                style={{
                  flex: 1, background: '#f1f5f9', color: deleteModal.isDeleting ? '#94a3b8' : '#475569', border: 'none', padding: '12px 24px',
                  borderRadius: '16px', fontSize: '15px', fontWeight: '700', cursor: deleteModal.isDeleting ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s', opacity: deleteModal.isDeleting ? 0.7 : 1
                }}
                onMouseEnter={(e) => !deleteModal.isDeleting && (e.currentTarget.style.background = '#e2e8f0')}
                onMouseLeave={(e) => !deleteModal.isDeleting && (e.currentTarget.style.background = '#f1f5f9')}
              >
                Batal
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteModal.isDeleting}
                style={{
                  flex: 1, background: deleteModal.isDeleting ? '#fca5a5' : '#ef4444', color: '#fff', border: 'none', padding: '12px 24px',
                  borderRadius: '16px', fontSize: '15px', fontWeight: '700', cursor: deleteModal.isDeleting ? 'not-allowed' : 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => !deleteModal.isDeleting && (e.currentTarget.style.background = '#dc2626')}
                onMouseLeave={(e) => !deleteModal.isDeleting && (e.currentTarget.style.background = '#ef4444')}
              >
                {deleteModal.isDeleting ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {errorModal.isOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
        }}>
          <div style={{
            background: '#fff', borderRadius: '24px', padding: '32px', width: '90%', maxWidth: '360px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            fontFamily: "'Inter', sans-serif", textAlign: 'center',
            animation: 'fadeIn 0.2s ease-out'
          }}>
            <div style={{
              width: '56px', height: '56px', borderRadius: '50%', background: '#fff1f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto'
            }}>
              <AlertCircle size={28} color="#e11d48" />
            </div>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '800', color: '#0f172a' }}>Gagal Dihapus</h3>
            <p style={{ margin: '0 0 32px 0', fontSize: '14px', color: '#64748b', lineHeight: '1.6' }}>
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal({ isOpen: false, message: '' })}
              style={{
                width: '100%', background: '#0f172a', color: '#fff', border: 'none', padding: '12px 24px',
                borderRadius: '16px', fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#1e293b'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#0f172a'}
            >
              Tutup
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectComments;
